import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { TableFooter, TablePagination } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled, useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as React from "react";
import { ReactComponent as EditIcon } from "../../assets/Companies/edit.svg";
import CreateCompany from "../../pages/Companies/CreateCompany/CreateCompany";
import { Company } from "../../types/Company";
import { getTranslation } from "../../utils/getTranslation";
import CircleAvatar from "../CircleAvatar/CircleAvatar";
import ModalWindow from "../ModalWindow/ModalWindow";
import s from "./CompaniesTable.module.css";

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? (
          <LastPageIcon color="success" />
        ) : (
          <FirstPageIcon color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight color="success" />
        ) : (
          <KeyboardArrowLeft color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft color="success" />
        ) : (
          <KeyboardArrowRight color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon color="success" />
        ) : (
          <LastPageIcon color="success" />
        )}
      </IconButton>
    </Box>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#313131",
    color: "white",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#2E2E2E",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#242424",
    border: 0,
  },
  // hide last border
  "td, th": {
    border: 0,
  },
}));

type CompaniesTableProps = {
  rows: Company[];
  page: number;
  rowsPerPage: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  selectCompaniesHandler: (id: string) => void;
  count: number;
  setReloadCompanyTriger: React.Dispatch<React.SetStateAction<number>>;
};
export default function CompaniesTable({
  rows,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  selectCompaniesHandler,
  count,
  setReloadCompanyTriger,
}: CompaniesTableProps) {
  const [updateCompanyModal, setUpdateCompanyModal] = React.useState(false);
  const [companyToUpdate, setCompanyToUpdate] = React.useState<
    Company | undefined
  >();

  React.useEffect(() => {
    if (updateCompanyModal) return;
    setCompanyToUpdate(undefined);
  }, [updateCompanyModal]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        minWidth: 700,
        width: "100%",
        borderRadius: "20px",
        backgroundColor: "transparent",
      }}
    >
      <Table
        sx={{ minWidth: 700, width: "100%" }}
        aria-label="customized table"
      >
        <TableHead className={s.tableHead}>
          <TableRow>
            <StyledTableCell align="left">Company name</StyledTableCell>
            <StyledTableCell align="left">Owner</StyledTableCell>
            <StyledTableCell align="left">Clients</StyledTableCell>
            <StyledTableCell align="left">Specialists</StyledTableCell>
            <StyledTableCell align="left">Programs</StyledTableCell>
            <StyledTableCell align="left">Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row: Company, index: number) => (
            <StyledTableRow
              key={index}
              sx={{
                cursor: "pointer",
              }}
              onClick={() => selectCompaniesHandler(row._id)}
            >
              <StyledTableCell align="left" className={s.nameColumn}>
                {row.name}
              </StyledTableCell>
              <StyledTableCell align="left" className={s.ownerColumn}>
                <div className={s.ownerBlock}>
                  <CircleAvatar userId={row.owner._id} />
                  <span>
                    {getTranslation(row.owner.name, "en") +
                      " " +
                      getTranslation(row.owner.surname, "en")}
                  </span>
                </div>
              </StyledTableCell>
              <StyledTableCell align="left" className={s.clientsColumn}>
                {row.clients.length}
              </StyledTableCell>
              <StyledTableCell align="left" className={s.specColumn}>
                {row.specialists.length}
              </StyledTableCell>
              <StyledTableCell align="left" className={s.programsColumn}>
                {row.projects.length}
              </StyledTableCell>
              <StyledTableCell align="left" className={s.programsColumn}>
                <div
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCompanyToUpdate(row);
                    setUpdateCompanyModal(true);
                  }}
                  className={s.editBtn}
                >
                  <EditIcon className={s.editIcon} />
                </div>{" "}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
        <TableFooter className={s.tableFooter}>
          <TableRow
            sx={{
              minWidth: 700,
              width: "100%",
              border: "none",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[5, 10, 15]}
              colSpan={6}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              sx={{ border: "none", color: "white" }}
            />
          </TableRow>
        </TableFooter>
      </Table>
      {companyToUpdate && (
        <ModalWindow
          setIsOpen={setUpdateCompanyModal}
          isOpen={updateCompanyModal}
          maxWidth="fit-content"
        >
          <CreateCompany
            setModalVisible={setUpdateCompanyModal}
            setPage={setPage}
            setReloadCompanyTriger={setReloadCompanyTriger}
            isUpdateCompany
            companyData={companyToUpdate}
          />
        </ModalWindow>
      )}
    </TableContainer>
  );
}
